import { Box, Stack } from '@mui/material';
import { useShallow } from 'zustand/react/shallow';
import { isTouchEnabled } from '../../../../../../../Theme';
import useWidth from '../../../../../../../components/Hooks/useWidth';
import useProductDetailsStore from '../../../../../../../stores/ProductDetails/productDetails.store';
import useTeceoStyles from '../../../../../../../useTeceoStyles';
import ConnectAccountModal from '../../../../../../login/ConnectAccountModal';
import OrderProductPriceV2 from '../../../../../../orders/products/product/components/OrderProductPriceV2';
import useOrderProductGridStyles from '../../../../../../orders/styles/useOrderProductGrid.styles';
import ProductDetailsAddOrderButtonV2 from '../../../../ProductDetailsAddOrderButtonV2';
import { getItemHeight } from '../../../../utils/productDetailsList.components';
import CombinationItemImage from '../../../KitDetailsList/components/CombinationItemImage';
import ProductDetailsCombinationColorAndTags from '../../../ProductDetailsCombinationColorAndTags/ProductDetailsCombinationColorAndTags';
import { CombinationLineProps } from '../../types/combinationLine.types';
import { getCombinationWithImageLineHeight } from '../../utils/combinationLine.utils';
import CombinationMassActionCheckbox from '../CombinationMassActionCheckbox/CombinationMassActionCheckbox';
import CombinationLineDesktopGrid from './CombinationLineDesktopGrid';
import useCombinationLineDesktopLayout from './hooks/useCombinationLineDesktopLayout';
import { useCombinationLineDesktopStyles } from './styles/combinationLineDesktop.styles';
import { useCombinationLineStyles } from './styles/combinationLineStyles.styles';

const CombinationLineDesktop = ({
  imageUrl,
  gridLength,
  isLoggedin,
  combinationTestId,
  isLoginModalOpen,
  infoSectionWidth,
  showProductImage,
  massActionProps,
  pasteStateButtonProps,
  hideCombinationPrice,
  orderProductPriceProps,
  orderProductGridContainerProps,
  productDetailsAddOrderButtonV2Props,
  productDetailsCombinationColorAndTagsProps,
  closeLoginModal,
}: CombinationLineProps) => {
  const { mobileWidth } = useWidth();
  const { isFinishedOrder, relatedProductsFilterState } = useProductDetailsStore(
    useShallow((state) => ({
      isFinishedOrder: state.productGridParams.isFinishedOrder,
      relatedProductsFilterState: state.relatedProductsFilterState,
    })),
  );
  const { infoWidth, infoMaxWidth, gridMinWidth } = useCombinationLineDesktopLayout({
    gridLength,
    infoSectionWidth,
  });

  const { classes } = useOrderProductGridStyles({ hasTouchGrid: isTouchEnabled });
  const { classes: layoutClasses } = useCombinationLineDesktopStyles({
    infoWidth,
    infoMaxWidth,
    gridMinWidth,
  });
  const { classes: teceoClasses } = useTeceoStyles();
  const { classes: itemClasses, cx } = useCombinationLineStyles({
    gridScrollerClassName: teceoClasses.hideScrollbar,
    height: relatedProductsFilterState
      ? getCombinationWithImageLineHeight(mobileWidth)
      : getItemHeight(mobileWidth),
  });

  return (
    <Stack
      direction="row"
      alignItems="center"
      test-id={combinationTestId}
      className={cx({
        [classes.root]: true,
        [itemClasses.root]: true,
        [itemClasses.combinationWithImagePadding]: relatedProductsFilterState,
        [itemClasses.combinationWithoutImagePadding]: !relatedProductsFilterState,
        [itemClasses.isSelected]: massActionProps?.isSelected,
      })}
    >
      <CombinationMassActionCheckbox massActionProps={massActionProps} />

      {!!showProductImage && <CombinationItemImage imageUrl={imageUrl} />}

      <Box className={layoutClasses.info}>
        <ProductDetailsCombinationColorAndTags
          {...productDetailsCombinationColorAndTagsProps}
          selloutSummary={orderProductGridContainerProps.selloutSummary}
        />
      </Box>

      <Box className={layoutClasses.grid}>
        <CombinationLineDesktopGrid
          gridLength={gridLength}
          pasteStateButtonProps={pasteStateButtonProps}
          orderProductGridContainerProps={orderProductGridContainerProps}
        />
      </Box>

      <Stack direction="row" alignItems="center" gap={1} className={layoutClasses.price}>
        {!hideCombinationPrice && (
          <OrderProductPriceV2 {...orderProductPriceProps} showTotal={false} />
        )}

        {productDetailsAddOrderButtonV2Props && !isFinishedOrder && (
          <ProductDetailsAddOrderButtonV2 {...productDetailsAddOrderButtonV2Props} />
        )}
      </Stack>

      {!isLoggedin && <ConnectAccountModal open={isLoginModalOpen} onClose={closeLoginModal} />}
    </Stack>
  );
};

export default CombinationLineDesktop;
